import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Review, ReviewFilter, SortOption } from '../types';
import { mensagemDeErro, reviewsService } from '../services';
import { motivoDaRecusa, respostaEmAnalise, respostaRecusada } from '../services/moderacao';
import styled from 'styled-components';
import {
  TextArea,
  FiltersModal,
  FiltersModalOverlay,
  FiltersModalContent,
  FiltersModalHeader,
  FiltersModalTitle,
  CloseButton,
  FiltersModalBody,
  FilterGroup,
  FilterLabel,
  CheckboxGroup,
  CheckboxLabel,
  DateInput,
  FiltersModalFooter,
  ApplyButton,
  ClearFiltersButton,
} from '../pages/styles';
import {
  AsideCard,
  Badge,
  Botao,
  BotaoPequeno,
  Hint,
  Lab,
} from '../../profiles-tracking/components/edit-profile-modal/components/EditorChrome';

type TabType = 'all' | 'responded' | 'not-responded';

/**
 * O backend grava este prefixo no erro quando o Google diz que a avaliação não
 * existe mais (apagada pelo autor ou pelo Google): não há o que responder.
 */
const MARCA_APAGADA_NO_GOOGLE = 'APAGADA_NO_GOOGLE';
const apagadaNoGoogle = (r: Review) => r.status === 'ERROR' && !!r.errorMsg?.startsWith(MARCA_APAGADA_NO_GOOGLE);

/* Prancheta ClienteComentarios: main 32/40, coluna 8/4, filtros rápidos, avaliações em cartão único. */
const Pagina = styled.div<{ $embedded?: boolean }>`
  padding: ${(p) => (p.$embedded ? '0' : '32px 40px')};
  display: flex;
  flex-direction: column;
  gap: 22px;
  min-width: 0;
  font-family: var(--font-sans);
  @media (max-width: 768px) {
    padding: ${(p) => (p.$embedded ? '0' : '20px 16px')};
  }
`;

const H1 = styled.h1`
  margin: 0;
  font-family: var(--font-sans);
  font-size: 32px;
  line-height: 40px;
  font-weight: 500;
  color: #1f1f1f;
`;

const Grade = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 8fr) minmax(0, 4fr);
  gap: 24px;
  align-items: start;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const FiltroRapido = styled.button<{ $on?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$on ? '#EAF1FB' : '#E2E6EA')};
  background: ${(p) => (p.$on ? '#EAF1FB' : '#fff')};
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => (p.$on ? '#0B4FA8' : '#2B2D31')};
  cursor: pointer;
  white-space: nowrap;
  .material-icons {
    font-size: 16px;
  }
`;

const Ordenar = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #e2e6ea;
  border-radius: 12px;
  background: #fff;
  .material-icons {
    font-size: 18px;
    color: #5f6368;
  }
  select {
    border: 0;
    outline: 0;
    font: inherit;
    font-size: 13px;
    color: #1f1f1f;
    background: transparent;
  }
`;

const Busca = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #e2e6ea;
  border-radius: 12px;
  background: #fff;
  flex: 1 1 240px;
  max-width: 360px;
  &:focus-within {
    border-color: #1264c7;
  }
  .material-icons {
    font-size: 18px;
    color: #5f6368;
  }
  input {
    border: 0;
    outline: 0;
    font: inherit;
    font-size: 13px;
    color: #1f1f1f;
    background: transparent;
    width: 100%;
    min-width: 0;
  }
`;

const Cartao = styled.section`
  background: #fff;
  border: 1px solid #e2e6ea;
  border-radius: 16px;
  overflow: hidden;
`;

const Avaliacao = styled.article`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 22px;
  border-bottom: 1px solid #eef1f4;
  &:last-child {
    border-bottom: 0;
  }
`;

const Avatar = styled.span<{ $tom: number }>`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: ${(p) => (p.$tom >= 4 ? '#EAF7EE' : p.$tom === 3 ? '#FFF7E0' : '#FCE8E6')};
  color: ${(p) => (p.$tom >= 4 ? '#137333' : p.$tom === 3 ? '#B06000' : '#B3261E')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;
`;

const Estrelas = styled.div`
  display: flex;
  gap: 2px;
  .material-icons {
    font-size: 16px;
    color: #f9ab00;
  }
  .material-icons.off {
    color: #e2e6ea;
  }
`;

const Resposta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  /* O texto pede ao menos 200px; no celular os botões Corrigir/Apagar descem em vez de espremê-lo. */
  > div:nth-child(2) {
    flex: 1 1 200px !important;
  }
  padding: 14px 16px;
  border-radius: 12px;
  background: #f7f9fc;
  font-size: 14px;
  line-height: 21px;
  > .material-icons {
    color: #0b4fa8;
    flex-shrink: 0;
  }
`;

const Editor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  textarea {
    width: 100%;
    min-height: 88px;
    padding: 12px 14px;
    border: 1px solid #e2e6ea;
    border-radius: 12px;
    font: inherit;
    font-size: 14px;
    line-height: 22px;
    color: #1f1f1f;
    box-sizing: border-box;
    resize: vertical;
  }
  textarea:focus,
  input:focus {
    outline: none;
    border-color: #1264c7;
    box-shadow: 0 0 0 2px rgba(18, 100, 199, 0.25);
  }
  input {
    width: 100%;
    height: 40px;
    padding: 0 12px;
    border: 1px solid #e2e6ea;
    border-radius: 12px;
    font: inherit;
    font-size: 13px;
    box-sizing: border-box;
  }
`;

const Kpis = styled.div`
  background: #fff;
  border: 1px solid #e2e6ea;
  border-radius: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  overflow: hidden;
  .kpi {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 16px 20px;
    border-right: 1px solid #eef1f4;
    min-width: 0;
  }
  .kpi:last-child {
    border-right: 0;
  }
  .kpi b {
    font-family: var(--font-sans);
    font-size: 28px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
`;

const Barra = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #3c4043;
  .material-icons {
    font-size: 14px;
    color: #f9ab00;
  }
  .t {
    flex: 1;
    height: 8px;
    border-radius: 999px;
    background: #f1f3f4;
    overflow: hidden;
  }
  .t > div {
    height: 100%;
    border-radius: 999px;
  }
`;

const Linha30 = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  b {
    font-weight: 500;
  }
`;

const Vazio = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #5f6368;
  .material-icons {
    font-size: 64px;
    color: #e2e6ea;
    margin-bottom: 16px;
  }
  h3 {
    font-family: var(--font-sans);
    font-size: 18px;
    font-weight: 500;
    margin: 0 0 8px;
    color: #1f1f1f;
  }
  p {
    margin: 0;
    font-size: 14px;
  }
`;

/** Quantas respostas dá para corrigir de uma vez: cada uma é uma chamada à IA e outra ao Google. */
export const LOTE_MAXIMO = 50;
/** Quantas sugestões a IA gera ao mesmo tempo no lote. */
const GERACOES_SIMULTANEAS = 3;

/** Avaliação ou resposta que contém o texto buscado, sem diferença de acento ou maiúscula. */
const semAcento = (texto: string) =>
  texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const INSTRUCAO_MAXIMA = 1000;

type EstadoDaCorrecao = 'gerando' | 'pronta' | 'falhou' | 'publicada' | 'erro';

type Correcao = {
  review: Review;
  sugestao: string;
  texto: string;
  incluir: boolean;
  estado: EstadoDaCorrecao;
  erro?: string;
};

type Props = {
  profileId: string;
  canEdit: boolean;
  /** Dentro do editor do perfil: sem o título grande da página. */
  embedded?: boolean;
};

const caixa: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E6EA',
  borderRadius: 16,
  padding: 20,
};

const rotulo: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#2B2D31',
  marginBottom: 6,
};


const ajuda: React.CSSProperties = {
  fontSize: 13,
  lineHeight: '18px',
  color: '#5F6368',
  margin: 0,
};

const selo = (fundo: string, cor: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  background: fundo,
  color: cor,
});

const dia = (iso: string) => iso.slice(0, 10);

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getAuthorInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();


export const ReviewsManager: React.FC<Props> = ({
  profileId,
  canEdit,
  embedded = false,
}) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [filters, setFilters] = useState<ReviewFilter>({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('most-recent');
  const [busca, setBusca] = useState('');

  // Resposta em edição: nova (responder) ou correção de uma já publicada.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');
  const [correctionNote, setCorrectionNote] = useState('');
  const [lastAiText, setLastAiText] = useState<string | null>(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [saving, setSaving] = useState(false);

  // Como responder às próximas.
  const [instrOpen, setInstrOpen] = useState(false);
  const [instrText, setInstrText] = useState('');
  const [instrWords, setInstrWords] = useState('');
  const [instrSaved, setInstrSaved] = useState<string | null>(null);
  const [savingInstr, setSavingInstr] = useState(false);
  const [sugerindoInstr, setSugerindoInstr] = useState(false);

  // Correção em lote.
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStep, setBulkStep] = useState<'motivo' | 'revisao'>('motivo');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkItems, setBulkItems] = useState<Correcao[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    setLoadError(null);
    try {
      setReviews(await reviewsService.list(profileId));
    } catch (error) {
      setLoadError(
        mensagemDeErro(error, 'Não foi possível carregar as avaliações.'),
      );
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    setSelected([]);
    setEditingId(null);
    load();
  }, [load]);

  useEffect(() => {
    if (!profileId) return;
    let ativo = true;
    reviewsService
      .getInstructions(profileId)
      .then((atual) => {
        if (!ativo) return;
        setInstrText(atual.instructions ?? '');
        setInstrWords(atual.preferredWords.join(', '));
        setInstrSaved(atual.instructions);
      })
      .catch(() => undefined);
    return () => {
      ativo = false;
    };
  }, [profileId]);

  const tabCounts = useMemo(() => {
    const total = reviews.length;
    const responded = reviews.filter((r) => r.response).length;
    const apagadas = reviews.filter((r) => !r.response && apagadaNoGoogle(r)).length;
    return { total, responded, notResponded: total - responded - apagadas };
  }, [reviews]);

  const filteredAndSortedReviews = useMemo(() => {
    let filtered = reviews.filter((r) =>
      activeTab === 'responded'
        ? !!r.response
        : activeTab === 'not-responded'
          ? !r.response && !apagadaNoGoogle(r)
          : true,
    );
    if (filters.rating && filters.rating.length > 0) {
      filtered = filtered.filter((r) => filters.rating!.includes(r.rating));
    }
    if (filters.startDate) {
      filtered = filtered.filter((r) => dia(r.date) >= filters.startDate!);
    }
    if (filters.endDate) {
      filtered = filtered.filter((r) => dia(r.date) <= filters.endDate!);
    }
    const termo = semAcento(busca.trim());
    if (termo) {
      filtered = filtered.filter((r) =>
        semAcento(`${r.authorName} ${r.comment ?? ''} ${r.response?.text ?? ''} ${r.draft ?? ''}`).includes(termo),
      );
    }
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        case 'highest-rating':
          return b.rating - a.rating;
        case 'lowest-rating':
          return a.rating - b.rating;
        default:
          return new Date(b.date).getTime() - new Date(a.date).getTime();
      }
    });
  }, [reviews, activeTab, filters, sortBy, busca]);

  const handleRatingFilterChange = (rating: number, checked: boolean) => {
    setFilters((prev) => {
      const currentRatings = prev.rating || [];
      const newRatings = checked
        ? [...currentRatings, rating]
        : currentRatings.filter((r) => r !== rating);
      return { ...prev, rating: newRatings.length > 0 ? newRatings : undefined };
    });
  };

  const fecharEditor = () => {
    setEditingId(null);
    setResponseText('');
    setCorrectionNote('');
    setLastAiText(null);
  };

  const abrirEditor = (review: Review) => {
    // Responder: parte do rascunho da IA, se houver. Corrigir: parte do que está no Google.
    const inicial = review.response?.text ?? review.draft ?? '';
    setEditingId(review.id);
    setResponseText(inicial);
    setCorrectionNote('');
    setLastAiText(review.response ? null : (review.draft ?? null));
  };

  const gerarComIA = async (review: Review) => {
    setGeneratingAI(true);
    try {
      const sugestao = await reviewsService.suggestReply(
        review.id,
        review.response ? correctionNote : undefined,
      );
      if (!sugestao.trim()) {
        toast.info('A IA não devolveu texto. Tente de novo.');
        return;
      }
      setResponseText(sugestao);
      setLastAiText(sugestao);
    } catch (error) {
      toast.error(mensagemDeErro(error, 'Não foi possível gerar a resposta.'));
    } finally {
      setGeneratingAI(false);
    }
  };

  const aplicarNaLista = (
    reviewId: string,
    texto: string,
    source: 'AI' | 'HUMAN',
    editada: boolean,
  ) => {
    setReviews((prev) =>
      prev.map((review) =>
        review.id === reviewId
          ? {
              ...review,
              status: 'REPLIED',
              draft: undefined,
              errorMsg: undefined,
              replyState: undefined,
              policyViolation: undefined,
              response: {
                id: review.id,
                text: texto,
                date: new Date().toISOString(),
                isEdited: editada,
                source,
              },
            }
          : review,
      ),
    );
  };

  const publicar = async (review: Review) => {
    const texto = responseText.trim();
    if (!texto) {
      toast.error('Escreva a resposta antes de publicar.');
      return;
    }
    const source = lastAiText && lastAiText.trim() === texto ? 'AI' : 'HUMAN';
    setSaving(true);
    try {
      await reviewsService.saveReply(review.id, texto, source);
      aplicarNaLista(review.id, texto, source, !!review.response);
      toast.success(
        review.response
          ? 'Resposta corrigida no Google.'
          : 'Resposta publicada no Google.',
      );
      fecharEditor();
    } catch (error) {
      toast.error(
        mensagemDeErro(error, 'Não foi possível publicar a resposta no Google.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const apagar = async (review: Review) => {
    if (!window.confirm('Apagar esta resposta no Google?')) return;
    try {
      await reviewsService.deleteReply(review.id);
      setReviews((prev) =>
        prev.map((r) =>
          r.id === review.id
            ? { ...r, status: 'WAITING_REPLY', response: undefined, replyState: undefined, policyViolation: undefined }
            : r,
        ),
      );
      setSelected((prev) => prev.filter((id) => id !== review.id));
      toast.success('Resposta apagada no Google.');
    } catch (error) {
      toast.error(mensagemDeErro(error, 'Não foi possível apagar a resposta.'));
    }
  };

  const salvarInstrucao = async () => {
    setSavingInstr(true);
    try {
      const salvo = await reviewsService.saveInstructions(profileId, {
        instructions: instrText.trim(),
        preferredWords: instrWords
          .split(',')
          .map((palavra) => palavra.trim())
          .filter(Boolean),
      });
      setInstrText(salvo.instructions ?? '');
      setInstrWords(salvo.preferredWords.join(', '));
      setInstrSaved(salvo.instructions);
      toast.success('Instrução salva. Vale para as próximas respostas.');
    } catch (error) {
      toast.error(mensagemDeErro(error, 'Não foi possível salvar a instrução.'));
    } finally {
      setSavingInstr(false);
    }
  };

  const sugerirInstrucao = async () => {
    setSugerindoInstr(true);
    try {
      const sugestao = await reviewsService.suggestInstructions(profileId);
      if (!sugestao.instructions?.trim()) {
        toast.info('A IA não devolveu sugestão. Tente de novo.');
        return;
      }
      setInstrText(sugestao.instructions.slice(0, INSTRUCAO_MAXIMA));
      if (sugestao.preferredWords.length) setInstrWords(sugestao.preferredWords.join(', '));
      toast.info('Sugestão preenchida. Revise e clique em "Salvar instrução".');
    } catch (error) {
      toast.error(mensagemDeErro(error, 'Não foi possível gerar a sugestão.'));
    } finally {
      setSugerindoInstr(false);
    }
  };

  // Respondidas que estão na lista agora (com filtros e busca): é o que "Selecionar todas" marca.
  const respondidasVisiveis = filteredAndSortedReviews.filter((r) => r.response);
  const todasVisiveisMarcadas =
    respondidasVisiveis.length > 0 &&
    respondidasVisiveis.slice(0, LOTE_MAXIMO).every((r) => selected.includes(r.id));

  const selecionarVisiveis = () => {
    if (todasVisiveisMarcadas) {
      setSelected([]);
      return;
    }
    const ids = respondidasVisiveis.slice(0, LOTE_MAXIMO).map((r) => r.id);
    setSelected(ids);
    if (respondidasVisiveis.length > LOTE_MAXIMO) {
      toast.info(
        `Marcadas as primeiras ${LOTE_MAXIMO} de ${respondidasVisiveis.length}. Corrija estas e depois selecione as próximas.`,
      );
    }
  };

  const alternarSelecao = (reviewId: string) => {
    setSelected((prev) => {
      if (prev.includes(reviewId)) return prev.filter((id) => id !== reviewId);
      if (prev.length >= LOTE_MAXIMO) {
        toast.info(`Corrija até ${LOTE_MAXIMO} respostas por vez.`);
        return prev;
      }
      return [...prev, reviewId];
    });
  };

  const abrirLote = () => {
    setBulkItems(
      reviews
        .filter((r) => selected.includes(r.id) && r.response)
        .map((review) => ({
          review,
          sugestao: '',
          texto: '',
          incluir: true,
          estado: 'gerando' as const,
        })),
    );
    setBulkNote('');
    setBulkStep('motivo');
    setBulkOpen(true);
  };

  const atualizarItem = (reviewId: string, mudanca: Partial<Correcao>) =>
    setBulkItems((prev) =>
      prev.map((item) =>
        item.review.id === reviewId ? { ...item, ...mudanca } : item,
      ),
    );

  const gerarLote = async () => {
    if (!bulkNote.trim()) {
      toast.error('Descreva o que estava errado nas respostas.');
      return;
    }
    setBulkStep('revisao');
    setBulkBusy(true);
    // Poucas por vez: rápido o bastante para 50 respostas sem estourar o limite de requisições da IA.
    const fila = [...bulkItems];
    const gerarProxima = async (): Promise<void> => {
      const item = fila.shift();
      if (!item) return;
      atualizarItem(item.review.id, { estado: 'gerando', erro: undefined });
      try {
        const sugestao = await reviewsService.suggestReply(
          item.review.id,
          bulkNote,
        );
        atualizarItem(
          item.review.id,
          sugestao.trim()
            ? { estado: 'pronta', sugestao, texto: sugestao, incluir: true }
            : { estado: 'falhou', incluir: false, erro: 'A IA não devolveu texto.' },
        );
      } catch (error) {
        atualizarItem(item.review.id, {
          estado: 'falhou',
          incluir: false,
          erro: mensagemDeErro(error, 'Falha ao gerar.'),
        });
      }
      return gerarProxima();
    };
    await Promise.all(
      Array.from({ length: Math.min(GERACOES_SIMULTANEAS, fila.length) }, gerarProxima),
    );
    setBulkBusy(false);
  };

  const publicarLote = async () => {
    const paraPublicar = bulkItems.filter(
      (item) => item.incluir && item.estado === 'pronta' && item.texto.trim(),
    );
    if (paraPublicar.length === 0) {
      toast.info('Nenhuma correção marcada para publicar.');
      return;
    }
    setBulkBusy(true);
    let publicadas = 0;
    for (const item of paraPublicar) {
      const texto = item.texto.trim();
      const source = texto === item.sugestao.trim() ? 'AI' : 'HUMAN';
      try {
        await reviewsService.saveReply(item.review.id, texto, source);
        aplicarNaLista(item.review.id, texto, source, true);
        atualizarItem(item.review.id, { estado: 'publicada' });
        publicadas += 1;
      } catch (error) {
        atualizarItem(item.review.id, {
          estado: 'erro',
          erro: mensagemDeErro(error, 'Falha ao publicar no Google.'),
        });
      }
    }
    setBulkBusy(false);
    setSelected([]);
    const falhas = paraPublicar.length - publicadas;
    if (falhas === 0) {
      toast.success(`${publicadas} resposta(s) corrigida(s) no Google.`);
    } else {
      toast.warning(
        `${publicadas} corrigida(s), ${falhas} com falha. Veja o motivo em cada uma.`,
      );
    }
  };

  const fecharLote = () => {
    if (bulkBusy) return;
    setBulkOpen(false);
    setBulkItems([]);
  };

  const prontas = bulkItems.filter(
    (item) => item.incluir && item.estado === 'pronta',
  ).length;
  const loteTerminou =
    bulkItems.length > 0 &&
    bulkItems.some((item) => item.estado === 'publicada' || item.estado === 'erro');
  const activeFilters =
    (filters.rating?.length || 0) +
    (filters.startDate ? 1 : 0) +
    (filters.endDate ? 1 : 0);

  const renderEditor = (review: Review) => {
    const corrigindo = !!review.response;
    return (
      <Editor>
        {corrigindo && (
          <>
            <label style={rotulo} htmlFor={`erro-${review.id}`}>
              O que estava errado? (opcional, orienta a IA ao refazer)
            </label>
            <input
              id={`erro-${review.id}`}
              value={correctionNote}
              maxLength={500}
              onChange={(e) => setCorrectionNote(e.target.value)}
              placeholder="Ex.: a resposta disse que abrimos aos domingos, e não abrimos"
              disabled={generatingAI || saving}
            />
          </>
        )}
        <textarea
          rows={3}
          value={responseText}
          onChange={(e) => setResponseText(e.target.value)}
          placeholder="Digite sua resposta ao comentário…"
          aria-label="Texto da resposta"
          disabled={generatingAI || saving}
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {lastAiText && lastAiText.trim() === responseText.trim() ? (
            <Badge>
              <span className="material-icons" aria-hidden="true">auto_awesome</span>
              Sugestão da IA com seu gabarito
            </Badge>
          ) : (
            <Hint>{corrigindo ? 'A correção substitui a resposta no Google.' : 'A IA escreve com base no seu gabarito; você revisa antes.'}</Hint>
          )}
          <span style={{ flex: 1 }} />
          <BotaoPequeno type="button" $variante="text" onClick={fecharEditor} disabled={saving}>
            Cancelar
          </BotaoPequeno>
          <BotaoPequeno type="button" $variante="outlined" onClick={() => gerarComIA(review)} disabled={generatingAI || saving}>
            <span className="material-icons" aria-hidden="true">auto_awesome</span>
            {generatingAI ? 'Gerando...' : corrigindo ? 'Refazer com IA' : lastAiText ? 'Gerar outra' : 'Gerar resposta'}
          </BotaoPequeno>
          <BotaoPequeno type="button" $variante="filled" onClick={() => publicar(review)} disabled={generatingAI || saving}>
            <span className="material-icons" aria-hidden="true">send</span>
            {saving ? 'Publicando...' : corrigindo ? 'Publicar correção' : 'Publicar resposta'}
          </BotaoPequeno>
        </div>
      </Editor>
    );
  };

  // ---------- Números do aside (prancheta): nota média, taxa de resposta, distribuição, últimos 30 dias ----------
  const total = reviews.length;
  const notaMedia = total ? reviews.reduce((soma, r) => soma + r.rating, 0) / total : 0;
  const taxaDeResposta = total ? Math.round((tabCounts.responded / total) * 100) : 0;
  const distribuicao = [5, 4, 3, 2, 1].map((nota) => ({ nota, quantos: reviews.filter((r) => r.rating === nota).length }));
  const limite30 = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const novas30 = reviews.filter((r) => new Date(r.date).getTime() >= limite30).length;
  const respondidasPelaIA30 = reviews.filter((r) => r.response?.source === 'AI' && new Date(r.response.date).getTime() >= limite30).length;
  const temposDeResposta = reviews
    .filter((r) => r.response?.date && r.date)
    .map((r) => new Date(r.response!.date).getTime() - new Date(r.date).getTime())
    .filter((ms) => Number.isFinite(ms) && ms >= 0);
  const tempoMedioHoras = temposDeResposta.length ? temposDeResposta.reduce((a, b) => a + b, 0) / temposDeResposta.length / 36e5 : null;
  const tempoMedioTexto = tempoMedioHoras === null ? '—' : tempoMedioHoras < 48 ? `${Math.round(tempoMedioHoras)} h` : `${Math.round(tempoMedioHoras / 24)} d`;
  const filtroDeNota = (filters.rating || []).slice().sort().join(',');
  const notasAltas = filtroDeNota === '4,5';
  const notasBaixas = filtroDeNota === '1,2,3';
  const alternarNotas = (notas: number[]) =>
    setFilters((prev) => {
      const atual = (prev.rating || []).slice().sort().join(',');
      const alvo = notas.slice().sort().join(',');
      return { ...prev, rating: atual === alvo ? undefined : notas };
    });
  const seloDaAvaliacao = (review: Review): { tom: 'verde' | 'amarelo' | 'azul' | 'cinza'; icone: string; texto: string; alerta?: boolean } =>
    respostaRecusada(review) && !apagadaNoGoogle(review)
      ? { tom: 'amarelo', icone: 'block', texto: 'Recusada pelo Google', alerta: true }
      : review.response && respostaEmAnalise(review)
      ? { tom: 'cinza', icone: 'hourglass_empty', texto: 'Em análise no Google' }
      : review.response
      ? { tom: 'verde' as const, icone: review.response.source === 'AI' ? 'auto_awesome' : 'check_circle', texto: review.response.source === 'AI' ? 'Respondida pela IA' : 'Respondida' }
      : apagadaNoGoogle(review)
        ? { tom: 'cinza' as const, icone: 'delete_outline', texto: 'Apagada no Google' }
      : review.status === 'ERROR'
        ? { tom: 'amarelo' as const, icone: 'error', texto: 'Falha ao responder' }
        : review.draft
          ? { tom: 'azul' as const, icone: 'auto_awesome', texto: 'Rascunho da IA' }
          : { tom: 'amarelo' as const, icone: 'schedule', texto: 'Sem resposta' };

  return (
    <Pagina
      $embedded={embedded}
      onKeyDown={(e) => {
        // Dentro do editor de perfil esta tela fica num <form>: Enter num campo
        // salvaria o perfil inteiro e fecharia o editor.
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') e.preventDefault();
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {!embedded && <H1>Avaliações</H1>}
          <Hint style={{ fontSize: 14 }}>
            Avaliações recebidas na ficha do Google. Responda aqui e a resposta é publicada na ficha; corrija respostas já publicadas e diga como a IA deve responder as próximas.
          </Hint>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canEdit && selected.length > 0 && (
            <>
              <Botao type="button" $variante="tonal" onClick={abrirLote}>
                <span className="material-icons" aria-hidden="true">auto_fix_high</span>
                Corrigir {selected.length} resposta(s)
              </Botao>
              <Botao type="button" $variante="outlined" onClick={() => setSelected([])}>
                Limpar seleção
              </Botao>
            </>
          )}
          <Botao type="button" $variante="outlined" onClick={load} disabled={loading}>
            <span className="material-icons" aria-hidden="true">refresh</span>
            {loading ? 'Atualizando...' : 'Atualizar'}
          </Botao>
        </div>
      </div>

      <Grade>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <FiltroRapido type="button" $on={activeTab === 'all'} aria-pressed={activeTab === 'all'} onClick={() => setActiveTab('all')}>
              {`Todas · ${tabCounts.total}`}
            </FiltroRapido>
            <FiltroRapido type="button" $on={activeTab === 'not-responded'} aria-pressed={activeTab === 'not-responded'} onClick={() => setActiveTab('not-responded')}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: '#D93025' }} />
              {`Não respondidas · ${tabCounts.notResponded}`}
            </FiltroRapido>
            <FiltroRapido type="button" $on={activeTab === 'responded'} aria-pressed={activeTab === 'responded'} onClick={() => setActiveTab('responded')}>
              {`Respondidas · ${tabCounts.responded}`}
            </FiltroRapido>
            <FiltroRapido type="button" $on={notasAltas} aria-pressed={notasAltas} onClick={() => alternarNotas([4, 5])}>
              <span className="material-icons" aria-hidden="true">star</span>
              4–5 estrelas
            </FiltroRapido>
            <FiltroRapido type="button" $on={notasBaixas} aria-pressed={notasBaixas} onClick={() => alternarNotas([1, 2, 3])}>
              <span className="material-icons" aria-hidden="true">star_half</span>
              1–3 estrelas
            </FiltroRapido>
            <span style={{ flex: 1 }} />
            <Ordenar>
              <span className="material-icons" aria-hidden="true">sort</span>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortOption)} aria-label="Ordenar avaliações">
                <option value="most-recent">Mais recentes</option>
                <option value="oldest">Mais antigas</option>
                <option value="highest-rating">Maior nota</option>
                <option value="lowest-rating">Menor nota</option>
              </select>
            </Ordenar>
            <FiltroRapido type="button" $on={Boolean(filters.startDate || filters.endDate)} onClick={() => setIsFilterModalOpen(true)}>
              <span className="material-icons" aria-hidden="true">tune</span>
              Período
              {activeFilters > 0 && <Badge style={{ height: 20 }}>{activeFilters}</Badge>}
            </FiltroRapido>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Busca>
              <span className="material-icons" aria-hidden="true">search</span>
              <input
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar no texto (ex.: marketing)"
                aria-label="Buscar no texto da avaliação ou da resposta"
              />
            </Busca>
            {canEdit && respondidasVisiveis.length > 0 && (
              <BotaoPequeno type="button" $variante="outlined" onClick={selecionarVisiveis}>
                <span className="material-icons" aria-hidden="true">
                  {todasVisiveisMarcadas ? 'check_box' : 'select_all'}
                </span>
                {todasVisiveisMarcadas
                  ? 'Desmarcar todas'
                  : `Selecionar respondidas da lista (${Math.min(respondidasVisiveis.length, LOTE_MAXIMO)})`}
              </BotaoPequeno>
            )}
            {busca.trim() && (
              <Hint>{`${filteredAndSortedReviews.length} ${filteredAndSortedReviews.length === 1 ? 'avaliação encontrada' : 'avaliações encontradas'}`}</Hint>
            )}
          </div>

          <Cartao>
            {loading && reviews.length === 0 ? (
              <Vazio role="status">Carregando avaliações...</Vazio>
            ) : loadError ? (
              <Vazio role="alert">
                <h3>Não foi possível carregar as avaliações</h3>
                <p style={{ marginBottom: 12 }}>{loadError}</p>
                <BotaoPequeno type="button" $variante="filled" onClick={load}>
                  <span className="material-icons" aria-hidden="true">refresh</span>
                  Tentar de novo
                </BotaoPequeno>
              </Vazio>
            ) : filteredAndSortedReviews.length === 0 ? (
              <Vazio>
                <span className="material-icons" aria-hidden="true">inbox</span>
                <h3>{reviews.length === 0 ? 'Este perfil ainda não tem avaliações importadas' : 'Nenhuma avaliação com esses filtros'}</h3>
                <p>{reviews.length === 0 ? 'As avaliações do Google entram aqui na sincronização diária.' : 'Ajuste os filtros para ver as demais.'}</p>
              </Vazio>
            ) : (
              filteredAndSortedReviews.map((review) => {
                const selo = seloDaAvaliacao(review);
                return (
                  <Avaliacao key={review.id}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      {canEdit && review.response && (
                        <input
                          type="checkbox"
                          checked={selected.includes(review.id)}
                          onChange={() => alternarSelecao(review.id)}
                          aria-label={`Selecionar a resposta para ${review.authorName} para corrigir em lote`}
                          style={{ width: 18, height: 18, marginTop: 11, accentColor: '#1264C7' }}
                        />
                      )}
                      <Avatar $tom={review.rating}>{getAuthorInitials(review.authorName)}</Avatar>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 500 }}>{review.authorName}</span>
                          <Hint>{formatDate(review.date)}</Hint>
                          <Badge $tom={selo.tom} style={{ marginLeft: 'auto', ...(selo.alerta ? { background: '#FCE8E6', color: '#B3261E' } : {}) }}>
                            <span className="material-icons" aria-hidden="true">{selo.icone}</span>
                            {selo.texto}
                          </Badge>
                        </div>
                        <Estrelas aria-label={`${review.rating} de 5 estrelas`}>
                          {Array.from({ length: 5 }, (_, i) => (
                            <span key={i} className={`material-icons${i < review.rating ? '' : ' off'}`} aria-hidden="true">star</span>
                          ))}
                        </Estrelas>
                        <p style={{ margin: '4px 0 0', fontSize: 14, lineHeight: '21px', color: '#2B2D31' }}>
                          {review.comment || 'Avaliação só com nota, sem comentário.'}
                        </p>
                      </div>
                    </div>

                    {review.response && editingId !== review.id && (
                      <Resposta>
                        <span className="material-icons" aria-hidden="true">reply</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
                          <Hint>
                            <b style={{ color: '#1F1F1F', fontWeight: 500 }}>Resposta do proprietário</b>
                            {review.response.date ? ` · ${formatDate(review.response.date)}` : ''}
                            {review.response.source === 'AI' ? ' · pela IA' : review.response.source === 'HUMAN' ? ' · pela equipe' : ''}
                            {review.response.isEdited ? ' · corrigida' : ''}
                          </Hint>
                          <span>{review.response.text}</span>
                          {respostaRecusada(review) && (
                            <Hint style={{ color: '#B3261E' }} role="alert">
                              {`O Google recusou esta resposta e ela não aparece na ficha${motivoDaRecusa(review.policyViolation) ? ` (motivo: ${motivoDaRecusa(review.policyViolation)})` : ''}. Escreva outra e publique de novo.`}
                            </Hint>
                          )}
                          {review.replyUrl && (
                            <a href={review.replyUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#1264C7', alignSelf: 'flex-start' }}>
                              Ver no Google
                            </a>
                          )}
                        </div>
                        {canEdit && (
                          <div style={{ display: 'flex', gap: 4, alignSelf: 'flex-start' }}>
                            <BotaoPequeno type="button" $variante={respostaRecusada(review) ? 'filled' : 'text'} onClick={() => abrirEditor(review)}>
                              <span className="material-icons" aria-hidden="true">{respostaRecusada(review) ? 'reply' : 'edit'}</span>
                              {respostaRecusada(review) ? 'Responder de novo' : 'Corrigir'}
                            </BotaoPequeno>
                            <BotaoPequeno type="button" $variante="text" style={{ color: '#B3261E' }} onClick={() => apagar(review)}>
                              <span className="material-icons" aria-hidden="true">delete</span>
                              Apagar
                            </BotaoPequeno>
                          </div>
                        )}
                      </Resposta>
                    )}

                    {!review.response && editingId !== review.id && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {apagadaNoGoogle(review) && (
                          <Hint>
                            Esta avaliação não existe mais no Google: foi apagada pelo autor ou pelo próprio Google. Não há o que responder.
                          </Hint>
                        )}
                        {respostaRecusada(review) && !apagadaNoGoogle(review) && (
                          <Hint style={{ color: '#B3261E' }} role="alert">
                            {`O Google recusou a resposta anterior${motivoDaRecusa(review.policyViolation) ? ` (motivo: ${motivoDaRecusa(review.policyViolation)})` : ''}.`}
                          </Hint>
                        )}
                        {review.status === 'ERROR' && !apagadaNoGoogle(review) && (
                          <Hint style={{ color: '#B3261E' }} role="alert">
                            A última tentativa de responder no Google falhou{review.errorMsg ? `: ${review.errorMsg}` : '.'}
                          </Hint>
                        )}
                        {review.draft && review.status !== 'ERROR' && (
                          <Resposta style={{ background: '#FBFDFF', border: '1px solid #D6E4F7' }}>
                            <span className="material-icons" aria-hidden="true">auto_awesome</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
                              <Hint><b style={{ color: '#1F1F1F', fontWeight: 500 }}>Rascunho da IA</b> · aguardando o envio automático</Hint>
                              <span>{review.draft}</span>
                            </div>
                          </Resposta>
                        )}
                        {canEdit && !apagadaNoGoogle(review) && (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <BotaoPequeno type="button" $variante="filled" onClick={() => abrirEditor(review)}>
                              <span className="material-icons" aria-hidden="true">reply</span>
                              {review.draft ? 'Revisar e responder' : 'Responder'}
                            </BotaoPequeno>
                          </div>
                        )}
                      </div>
                    )}

                    {editingId === review.id && renderEditor(review)}
                  </Avaliacao>
                );
              })
            )}
          </Cartao>
        </div>

        <aside style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          <Kpis>
            <div className="kpi">
              <Hint>Nota média</Hint>
              <b style={{ color: notaMedia >= 4 ? '#137333' : notaMedia >= 3 ? '#B06000' : '#B3261E' }}>{total ? notaMedia.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—'}</b>
              <Hint>{`${total} ${total === 1 ? 'avaliação' : 'avaliações'}`}</Hint>
            </div>
            <div className="kpi">
              <Hint>Taxa de resposta</Hint>
              <b>{total ? `${taxaDeResposta}%` : '—'}</b>
              {tabCounts.notResponded > 0 ? (
                <Badge $tom="amarelo" style={{ alignSelf: 'flex-start' }}>{`${tabCounts.notResponded} ${tabCounts.notResponded === 1 ? 'pendente' : 'pendentes'}`}</Badge>
              ) : (
                <Hint>tudo respondido</Hint>
              )}
            </div>
          </Kpis>

          <AsideCard style={{ gap: 10 }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 15, fontWeight: 600 }}>Distribuição</span>
            {distribuicao.map((item) => (
              <Barra key={item.nota}>
                <span style={{ width: 14 }}>{item.nota}</span>
                <span className="material-icons" aria-hidden="true">star</span>
                <div className="t">
                  <div style={{ width: `${total ? (item.quantos / total) * 100 : 0}%`, background: item.nota >= 4 ? '#1E8E3E' : item.nota === 3 ? '#F9AB00' : '#D93025' }} />
                </div>
                <span style={{ width: 28, textAlign: 'right' }}>{item.quantos}</span>
              </Barra>
            ))}
          </AsideCard>

          <AsideCard style={{ gap: 10 }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 15, fontWeight: 600 }}>Últimos 30 dias</span>
            <Linha30><span>Novas avaliações</span><b>{novas30}</b></Linha30>
            <Linha30><span>Respondidas pela IA</span><b>{respondidasPelaIA30}</b></Linha30>
            <Linha30><span>Tempo médio de resposta</span><b>{tempoMedioTexto}</b></Linha30>
          </AsideCard>

          <AsideCard aria-labelledby="como-responder" style={{ gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <Lab id="como-responder" style={{ textTransform: 'none', fontSize: 15, fontWeight: 600, letterSpacing: 0, color: '#1F1F1F' }}>
                <span className="material-icons" style={{ color: '#0B4FA8' }}>auto_awesome</span>
                Como a IA responde
              </Lab>
              <BotaoPequeno type="button" $variante="outlined" onClick={() => setInstrOpen((aberto) => !aberto)} aria-expanded={instrOpen}>
                <span className="material-icons" aria-hidden="true">{instrOpen ? 'expand_less' : 'tune'}</span>
                {instrOpen ? 'Fechar' : instrSaved ? 'Ver ou alterar' : 'Definir instrução'}
              </BotaoPequeno>
            </div>
            <Hint>
              {instrSaved
                ? 'A IA segue a instrução abaixo nas respostas automáticas e nas sugestões.'
                : 'Sem instrução: a IA responde no tom padrão (agradece, é cordial e cita o negócio).'}
            </Hint>
            {instrOpen && (
              <Editor>
                <label style={rotulo} htmlFor="instrucao-resposta">Instrução para a IA</label>
                <textarea
                  id="instrucao-resposta"
                  rows={4}
                  value={instrText}
                  maxLength={INSTRUCAO_MAXIMA}
                  onChange={(e) => setInstrText(e.target.value)}
                  placeholder="Ex.: usar tom formal; agradecer sempre pelo nome; nunca prometer prazo de entrega; em avaliação negativa, pedir que o cliente nos chame por mensagem."
                  disabled={!canEdit || savingInstr}
                />
                <Hint>{`${instrText.length}/${INSTRUCAO_MAXIMA}. A IA não inclui telefone, WhatsApp nem link nas respostas, mesmo que a instrução peça.`}</Hint>
                <label style={rotulo} htmlFor="palavras-resposta">Palavras para usar quando couber (separadas por vírgula)</label>
                <input
                  id="palavras-resposta"
                  value={instrWords}
                  onChange={(e) => setInstrWords(e.target.value)}
                  placeholder="Ex.: pontualidade, garantia, equipe especializada"
                  disabled={!canEdit || savingInstr}
                />
                {canEdit && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <BotaoPequeno type="button" $variante="tonal" onClick={sugerirInstrucao} disabled={sugerindoInstr || savingInstr}>
                      <span className="material-icons" aria-hidden="true">auto_awesome</span>
                      {sugerindoInstr ? 'Gerando...' : 'Sugerir com IA'}
                    </BotaoPequeno>
                    <BotaoPequeno type="button" $variante="filled" onClick={salvarInstrucao} disabled={savingInstr || sugerindoInstr}>
                      <span className="material-icons" aria-hidden="true">save</span>
                      {savingInstr ? 'Salvando...' : 'Salvar instrução'}
                    </BotaoPequeno>
                  </div>
                )}
                <Hint>"Sugerir com IA" preenche os campos a partir da categoria, da descrição e das palavras do Gabarito; nada é salvo antes de você revisar. Vale para as próximas respostas. Para consertar respostas já publicadas, use "Corrigir" em cada uma ou busque um termo e selecione várias.</Hint>
              </Editor>
            )}
          </AsideCard>
        </aside>
      </Grade>

      {isFilterModalOpen && (
        <FiltersModal $isOpen={isFilterModalOpen}>
          <FiltersModalOverlay onClick={() => setIsFilterModalOpen(false)}>
            <FiltersModalContent onClick={(e) => e.stopPropagation()}>
              <FiltersModalHeader>
                <FiltersModalTitle>Período e nota</FiltersModalTitle>
                <CloseButton type="button"
                  onClick={() => setIsFilterModalOpen(false)}
                  aria-label="Fechar filtros"
                >
                  <span className="material-icons" aria-hidden="true">close</span>
                </CloseButton>
              </FiltersModalHeader>

              <FiltersModalBody>
                <FilterGroup>
                  <FilterLabel>Nota</FilterLabel>
                  <CheckboxGroup>
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <CheckboxLabel key={rating}>
                        <input
                          type="checkbox"
                          checked={filters.rating?.includes(rating) || false}
                          onChange={(e) =>
                            handleRatingFilterChange(rating, e.target.checked)
                          }
                          aria-label={`${rating} estrela(s)`}
                        />
                        <div style={{ display: 'flex', gap: '2px' }}>
                          {Array.from({ length: rating }, (_, i) => (
                            <span
                              key={i}
                              className="material-icons"
                              style={{ fontSize: '16px', color: '#F9AB00' }}
                              aria-hidden="true"
                            >
                              star
                            </span>
                          ))}
                        </div>
                      </CheckboxLabel>
                    ))}
                  </CheckboxGroup>
                </FilterGroup>

                <FilterGroup>
                  <FilterLabel>Data inicial</FilterLabel>
                  <DateInput
                    type="date"
                    value={filters.startDate || ''}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, startDate: e.target.value }))
                    }
                  />
                </FilterGroup>

                <FilterGroup>
                  <FilterLabel>Data final</FilterLabel>
                  <DateInput
                    type="date"
                    value={filters.endDate || ''}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, endDate: e.target.value }))
                    }
                  />
                </FilterGroup>
              </FiltersModalBody>

              <FiltersModalFooter>
                <ClearFiltersButton type="button" onClick={() => setFilters({})}>
                  Limpar filtros
                </ClearFiltersButton>
                <ApplyButton type="button" onClick={() => setIsFilterModalOpen(false)}>
                  Aplicar
                </ApplyButton>
              </FiltersModalFooter>
            </FiltersModalContent>
          </FiltersModalOverlay>
        </FiltersModal>
      )}

      {bulkOpen && (
        <FiltersModal $isOpen={bulkOpen}>
          <FiltersModalOverlay onClick={fecharLote}>
            <FiltersModalContent
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 760, width: '94vw' }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="titulo-lote"
            >
              <FiltersModalHeader>
                <FiltersModalTitle id="titulo-lote">
                  Corrigir {bulkItems.length} resposta(s)
                </FiltersModalTitle>
                <CloseButton type="button" onClick={fecharLote} aria-label="Fechar" disabled={bulkBusy}>
                  <span className="material-icons" aria-hidden="true">close</span>
                </CloseButton>
              </FiltersModalHeader>

              <FiltersModalBody style={{ maxHeight: '64vh', overflowY: 'auto' }}>
                {bulkStep === 'motivo' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <label style={rotulo} htmlFor="motivo-lote">
                      O que estava errado nessas respostas?
                    </label>
                    <TextArea
                      id="motivo-lote"
                      value={bulkNote}
                      maxLength={500}
                      onChange={(e) => setBulkNote(e.target.value)}
                      placeholder="Ex.: as respostas citaram entrega grátis, e nós não fazemos entrega"
                    />
                    <p style={ajuda}>
                      A IA refaz cada resposta corrigindo esse erro e seguindo a
                      instrução do perfil. Nada vai para o Google antes de você
                      revisar a lista.
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {bulkItems.map((item) => (
                      <div key={item.review.id} style={{ ...caixa, padding: 14 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            marginBottom: 8,
                          }}
                        >
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              fontSize: 14,
                              fontWeight: 600,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={item.incluir}
                              disabled={item.estado !== 'pronta' || bulkBusy}
                              onChange={(e) =>
                                atualizarItem(item.review.id, {
                                  incluir: e.target.checked,
                                })
                              }
                              style={{ width: 18, height: 18 }}
                            />
                            {item.review.authorName} · {item.review.rating} estrela(s)
                          </label>
                          {item.estado === 'gerando' && (
                            <span style={selo('#EAF1FB', '#0B4FA8')}>Gerando...</span>
                          )}
                          {item.estado === 'falhou' && (
                            <span style={selo('#FCE8E6', '#B3261E')}>Não gerou</span>
                          )}
                          {item.estado === 'publicada' && (
                            <span style={selo('#EAF7EE', '#1E8E3E')}>Publicada</span>
                          )}
                          {item.estado === 'erro' && (
                            <span style={selo('#FCE8E6', '#B3261E')}>Falhou ao publicar</span>
                          )}
                        </div>
                        {item.review.comment && (
                          <p style={{ ...ajuda, marginBottom: 8 }}>
                            Avaliação: {item.review.comment}
                          </p>
                        )}
                        <p style={{ ...ajuda, marginBottom: 8 }}>
                          Resposta atual: {item.review.response?.text}
                        </p>
                        {(item.estado === 'pronta' ||
                          item.estado === 'publicada' ||
                          item.estado === 'erro') && (
                          <TextArea
                            value={item.texto}
                            onChange={(e) =>
                              atualizarItem(item.review.id, { texto: e.target.value })
                            }
                            aria-label={`Nova resposta para ${item.review.authorName}`}
                            disabled={item.estado !== 'pronta' || bulkBusy}
                          />
                        )}
                        {item.erro && (
                          <p style={{ ...ajuda, color: '#B3261E', marginTop: 6 }} role="alert">
                            {item.erro}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </FiltersModalBody>

              <FiltersModalFooter>
                {bulkStep === 'revisao' && bulkItems.some((item) => item.estado === 'gerando') && (
                  <span role="status" style={{ ...ajuda, marginRight: 'auto', alignSelf: 'center' }}>
                    {`${bulkItems.filter((item) => item.estado !== 'gerando').length} de ${bulkItems.length} prontas`}
                  </span>
                )}
                <ClearFiltersButton type="button" onClick={fecharLote} disabled={bulkBusy}>
                  {loteTerminou ? 'Fechar' : 'Cancelar'}
                </ClearFiltersButton>
                {bulkStep === 'motivo' ? (
                  <ApplyButton type="button" onClick={gerarLote}>Gerar novas respostas</ApplyButton>
                ) : (
                  !loteTerminou && (
                    <ApplyButton type="button" onClick={publicarLote} disabled={bulkBusy || prontas === 0}>
                      {bulkBusy ? 'Aguarde...' : `Publicar ${prontas} correção(ões)`}
                    </ApplyButton>
                  )
                )}
              </FiltersModalFooter>
            </FiltersModalContent>
          </FiltersModalOverlay>
        </FiltersModal>
      )}
    </Pagina>
  );
};

export default ReviewsManager;
